'use client'

import { differenceInCalendarDays, format, parseISO } from 'date-fns'
import { Gift } from 'lucide-react'
import { useMemo } from 'react'

import { useRedeemT } from '../i18n/useRedeemT'
import type { IGiftDetail } from '../types/schema'
import { decodeEntities } from '../utils/decodeEntities'
import { spaToday } from '../utils/timeSlots'

interface Props {
  gift: IGiftDetail
}

/** The dark gift band, with a real expiry countdown. */
export default function GiftDetailsCard({ gift }: Props) {
  const t = useRedeemT()

  const { expiresLabel, daysLeft } = useMemo(() => {
    const expiry = parseISO(gift.expired)

    if (Number.isNaN(expiry.valueOf())) {
      return { expiresLabel: gift.expired, daysLeft: null }
    }

    return {
      expiresLabel: format(expiry, 'd MMM yyyy'),
      daysLeft: differenceInCalendarDays(expiry, parseISO(spaToday()))
    }
  }, [gift.expired])

  // `product` arrives HTML-entity-encoded from the DB.
  const product = useMemo(() => decodeEntities(gift.product), [gift.product])

  return (
    <section className='container mx-auto max-w-4xl px-4 py-6'>
      <div className='flex flex-col justify-between gap-4 rounded-xl bg-theme p-6 text-white shadow-lg md:flex-row md:items-center md:p-8'>
        <div>
          <div className='flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-theme-100'>
            <Gift className='h-3.5 w-3.5' /> {t('LABEL_YOUR_EGIFT')}
          </div>
          <h2 className='mt-1 text-2xl font-bold'>{product}</h2>
          <p className='mt-2 text-sm text-theme-100'>
            {t('LABEL_GIFT_META', { number: gift.gift_number, sender: gift.receive_from })}
          </p>
        </div>

        <div className='md:text-right'>
          <div className='text-[11px] font-bold uppercase tracking-widest text-theme-100'>
            {t('LABEL_INPUT_GIFT_EXPIRED')}
          </div>
          <div className='text-lg font-bold text-theme-orange'>{expiresLabel}</div>
          {daysLeft !== null && daysLeft >= 0 && (
            <div className='mt-1 text-xs text-theme-100'>
              {daysLeft === 0
                ? t('LABEL_GIFT_EXPIRES_TODAY')
                : t('LABEL_GIFT_EXPIRES_IN_DAYS', { days: daysLeft })}
            </div>
          )}
        </div>
      </div>
    </section>
  )
}
